import React, { useState, useEffect, useCallback, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Alert } from "reactstrap";
import {
  Button,
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
} from "../../../components/Component";
import { useHistory, useParams } from "react-router-dom";
import http from "../../../utils/http-common";
import swal from "sweetalert";
import { getPublicUrl, API_BASE_URL } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import LicenseEditors from "../../../components/licenses/LicenseEditors";
import ProfilePhotoBlock from "../../../components/features/practitioner/ProfilePhotoBlock";

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

/** Open uploaded clinic/partner files (paths stored relative to API origin). */
function resolveBackendAssetUrl(relativeOrAbsolute) {
  const u = String(relativeOrAbsolute || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = String(API_BASE_URL || "").replace(/\/$/, "");
  const pathPart = u.startsWith("/") ? u : `/${u}`;
  return `${base}${pathPart}`;
}

function parseCommaSeparatedLangs(s) {
  if (!s || typeof s !== "string") return [];
  return s
    .split(/[\s,]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
    .map((c) => c.slice(0, 8));
}

/** Resolve 24-char user id or practitioner email via partner lookup (same as partner portal team page). */
async function resolveAssignUserId(raw) {
  const t = String(raw || "").trim();
  if (!t) return "";
  if (OBJECT_ID_RE.test(t)) return t;
  if (!t.includes("@")) return t;
  const res = await http.get("/api/v1/partners/medical/users/lookup", { params: { q: t } });
  const u = res.data?.data;
  return u?._id ? String(u._id) : "";
}

/**
 * Admin: practitioners for a single clinic (TC authorization, compliance fields, link to another clinic).
 * Route: /clinics/:clinicId/practitioners
 */
const ClinicPractitionersAdmin = () => {
  const { clinicId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [clinicsSummary, setClinicsSummary] = useState([]);
  const [practitionerBusyId, setPractitionerBusyId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [partnerPractitionerPool, setPartnerPractitionerPool] = useState([]);
  const [rosterSearch, setRosterSearch] = useState("");
  const [rosterPickUserId, setRosterPickUserId] = useState("");
  const [lookupQuery, setLookupQuery] = useState("");
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [addForm, setAddForm] = useState({
    displayName: "",
    role: "doctor",
    userIdOrEmail: "",
    diplomas: "",
    consultationLanguages: "",
    primaryConsultationLanguage: "",
  });

  const isStaffRole = (role) => ["nurse", "assistant"].includes(String(role || "").toLowerCase());

  const loadData = useCallback(async (signal, options = {}) => {
    const { showSpinner = true } = options;
    if (!clinicId) return;
    if (showSpinner) setLoading(true);
    try {
      const clinicRes = await http.get(`/api/v1/partners/medical/clinics/${clinicId}`, { signal });
      const clinicData = clinicRes.data?.data || clinicRes.data;
      setClinic(clinicData);
      const pid = clinicData?.partner?._id || clinicData?.partner;
      if (!pid) {
        setPartnerId(null);
        setPractitioners([]);
        setClinicsSummary([]);
        return;
      }
      setPartnerId(String(pid));
      const res = await http.get(`/api/v1/medical-partners/${pid}`, {
        params: { include: "practitioners" },
        signal,
        suppressInlineErrorSwal: true,
      });
      const all = Array.isArray(res.data?.practitioners) ? res.data.practitioners : [];
      setPartnerPractitionerPool(all);
      const forClinic = all.filter(
        (p) => String(p.clinicId || p.clinic || "") === String(clinicId)
      );
      setPractitioners(forClinic);
      setClinicsSummary(Array.isArray(res.data?.clinicsSummary) ? res.data.clinicsSummary : []);
    } catch (e) {
      if (isAbortError(e)) return;
      swal("Could not load practitioners", formatAdminApiError(e, "Failed to load clinic practitioners"), "error");
      setClinic(null);
      setPractitioners([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    if (!clinicId) return undefined;
    const ac = new AbortController();
    loadData(ac.signal, { showSpinner: true });
    return () => ac.abort();
  }, [clinicId, loadData]);

  const reloadAfterMutation = useCallback(
    () => loadData(undefined, { showSpinner: false }),
    [loadData]
  );

  const navigateBackFromClinicContext = useCallback(() => {
    if (partnerId) {
      history.push(getPublicUrl(`medical-partner-details/${partnerId}`));
      return;
    }
    history.push(getPublicUrl("clinics"));
  }, [history, partnerId]);

  const legacyWithoutUserCount = useMemo(
    () => practitioners.filter((p) => !p.user).length,
    [practitioners]
  );

  const userIdsOnThisClinic = useMemo(() => {
    const s = new Set();
    practitioners.forEach((p) => {
      const u = p.user?._id || p.user;
      if (u) s.add(String(u));
    });
    return s;
  }, [practitioners]);

  const rosterCandidates = useMemo(() => {
    const q = rosterSearch.trim().toLowerCase();
    return partnerPractitionerPool.filter((p) => {
      const uid = p.user?._id || p.user;
      if (!uid) return false;
      if (userIdsOnThisClinic.has(String(uid))) return false;
      if (!q) return true;
      const email = (p.user?.email && String(p.user.email).toLowerCase()) || "";
      const name = [p.user?.first_name, p.user?.last_name, p.displayName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return email.includes(q) || name.includes(q) || String(uid).toLowerCase().includes(q);
    });
  }, [partnerPractitionerPool, userIdsOnThisClinic, rosterSearch]);

  const openAddModal = () => {
    setAddForm({
      displayName: "",
      role: "doctor",
      userIdOrEmail: "",
      diplomas: "",
      consultationLanguages: "",
      primaryConsultationLanguage: "",
    });
    setRosterSearch("");
    setRosterPickUserId("");
    setLookupQuery("");
    setVerifiedUser(null);
    setAddModalOpen(true);
  };

  const verifyLookup = async () => {
    const raw = lookupQuery.trim();
    if (!raw) {
      swal("Validation", "Enter an email or 24-character user id.", "warning");
      return;
    }
    setVerifyBusy(true);
    setVerifiedUser(null);
    try {
      const res = await http.get("/api/v1/partners/medical/users/lookup", {
        params: { q: raw },
        suppressInlineErrorSwal: true,
      });
      const u = res.data?.data;
      if (u && u._id) {
        setVerifiedUser(u);
        setRosterPickUserId("");
      } else {
        swal("Lookup", "Unexpected response from server.", "error");
      }
    } catch (e) {
      if (e.response?.status === 404) {
        setVerifiedUser(null);
        swal(
          "Not found",
          "No user matches this email or id. You can send an invitation to a valid email address instead.",
          "info"
        );
      } else {
        swal("Lookup failed", formatAdminApiError(e, "Request failed"), "error");
      }
    } finally {
      setVerifyBusy(false);
    }
  };

  const sendInvitationToEmail = async () => {
    const email = lookupQuery.trim();
    if (!email || !email.includes("@")) {
      swal("Validation", "Enter a valid email to invite.", "warning");
      return;
    }
    setInviteBusy(true);
    try {
      await http.post(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/invite`,
        { email, role: addForm.role || "doctor" },
        { suppressInlineErrorSwal: true }
      );
      swal("Invitation sent", "The practitioner will receive an email to join this clinic.", "success");
    } catch (e) {
      swal("Could not send invitation", formatAdminApiError(e, "Invite failed"), "error");
    } finally {
      setInviteBusy(false);
    }
  };

  const submitAddPractitioner = async () => {
    if (!clinicId) return;
    const diplomasArr = addForm.diplomas
      ? addForm.diplomas
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const langs = parseCommaSeparatedLangs(addForm.consultationLanguages);
    const primaryRaw = addForm.primaryConsultationLanguage && String(addForm.primaryConsultationLanguage).trim().toLowerCase();
    const primaryLang = primaryRaw ? primaryRaw.slice(0, 8) : undefined;
    setAddSubmitting(true);
    try {
      let uid = rosterPickUserId || "";
      const verifiedOk = verifiedUser && verifiedUser.role === "practitioner";
      if (!uid && verifiedOk) {
        uid = String(verifiedUser._id);
      }
      if (!uid) {
        uid = await resolveAssignUserId(addForm.userIdOrEmail);
      }
      if (!uid) {
        swal(
          "Validation",
          "Pick a practitioner from the partner list, verify an email / user id, or paste a valid id in the optional legacy field.",
          "warning"
        );
        return;
      }
      if (verifiedUser && verifiedUser.role !== "practitioner") {
        swal(
          "Wrong account type",
          "This user exists but is not a practitioner. Change their role to practitioner before adding them to the clinic.",
          "warning"
        );
        return;
      }
      if (
        uid &&
        !rosterPickUserId &&
        !(verifiedUser && verifiedUser.role === "practitioner")
      ) {
        try {
          const roleRes = await http.get("/api/v1/partners/medical/users/lookup", {
            params: { q: uid },
            suppressInlineErrorSwal: true,
          });
          const u = roleRes.data?.data;
          if (u?.role && u.role !== "practitioner") {
            swal(
              "Wrong account type",
              "This user is not a practitioner account. Change their role to practitioner or use Verify.",
              "warning"
            );
            return;
          }
        } catch (roleErr) {
          if (roleErr.response?.status === 404) {
            swal("User not found", "Check the id or email and try again.", "warning");
            return;
          }
        }
      }
      const body = {
        user: uid,
        role: addForm.role || "doctor",
        displayName: addForm.displayName.trim() || undefined,
        diplomas: diplomasArr,
      };
      if (langs.length > 0) body.consultationLanguages = langs;
      if (primaryLang) body.primaryConsultationLanguage = primaryLang;
      await http.post(`/api/v1/partners/medical/clinics/${clinicId}/practitioners`, body, {
        suppressInlineErrorSwal: true,
      });
      setAddModalOpen(false);
      await reloadAfterMutation();
      swal("Success", "Practitioner added to this clinic.", "success");
    } catch (e) {
      swal("Could not add practitioner", formatAdminApiError(e, "Failed to add practitioner"), "error");
    } finally {
      setAddSubmitting(false);
    }
  };

  const setPractitionerTcAuthorized = async (row, nextVal) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    setPractitionerBusyId(String(row._id));
    try {
      await http.put(
        `/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}`,
        { teleconsultationAuthorized: nextVal },
        { suppressInlineErrorSwal: true }
      );
      await reloadAfterMutation();
    } catch (e) {
      swal(
        "Teleconsultation setting could not be updated",
        formatAdminApiError(e, "Update failed"),
        "error"
      );
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const updatePractitionerCompliance = async (row, payload) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    setPractitionerBusyId(String(row._id));
    try {
      await http.put(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}`, payload, {
        suppressInlineErrorSwal: true,
      });
      await reloadAfterMutation();
    } catch (e) {
      swal("Practitioner could not be updated", formatAdminApiError(e, "Update failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const uploadLicenceDocument = async (row, file) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id || !file) return;
    setPractitionerBusyId(String(row._id));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post(
        `/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}/licence-document`,
        fd,
        { suppressInlineErrorSwal: true }
      );
      await reloadAfterMutation();
      swal("Success", "Licence document uploaded.", "success");
    } catch (e) {
      swal("Upload failed", formatAdminApiError(e, "Upload failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const deleteLicenceDocument = async (row) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    setPractitionerBusyId(String(row._id));
    try {
      await http.delete(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}/licence-document`, {
        suppressInlineErrorSwal: true,
      });
      await reloadAfterMutation();
      swal("Success", "Licence document removed.", "success");
    } catch (e) {
      swal("Could not remove document", formatAdminApiError(e, "Remove failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const removePractitionerFromClinic = (row) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    swal({
      title: "Remove from clinic?",
      text: "This removes the practitioner row for this clinic only.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) return;
      setPractitionerBusyId(String(row._id));
      http
        .delete(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}`, {
          suppressInlineErrorSwal: true,
        })
        .then(() => reloadAfterMutation())
        .then(() => swal("Done", "Practitioner removed from clinic", "success"))
        .catch((e) => swal("Could not remove practitioner", formatAdminApiError(e, "Remove failed"), "error"))
        .finally(() => setPractitionerBusyId(null));
    });
  };

  const linkPractitionerToClinic = async (row, targetClinicId) => {
    const uid = row.user?._id || row.user;
    if (!targetClinicId || !uid) {
      swal("Error", "A linked user account is required to add this practitioner to another clinic.", "error");
      return;
    }
    setPractitionerBusyId(String(row._id));
    try {
      await http.post(
        `/api/v1/partners/medical/clinics/${targetClinicId}/practitioners`,
        {
          user: uid,
          role: row.role || "doctor",
          teleconsultationAuthorized: false,
        },
        { suppressInlineErrorSwal: true }
      );
      await reloadAfterMutation();
      swal("Success", "Practitioner linked to clinic", "success");
    } catch (e) {
      swal("Could not link practitioner to clinic", formatAdminApiError(e, "Link failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const renderTable = (rows, title) => (
    <>
      <BlockHead size="sm" className="mb-2">
        <BlockTitle tag="h5">{title}</BlockTitle>
      </BlockHead>
      {rows.length === 0 ? (
        <p className="text-soft mb-4">No rows in this category.</p>
      ) : (
        <DataTable className="card-stretch mb-4" scrollable>
          <DataTableBody compact>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">Name</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Photo</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">TC OK</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Languages / primary</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Country</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Licenses</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Diplomas (list)</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Diploma notes</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Licence document</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text" title="Agent attestation after registry check">
                  Licence verified
                </span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Role</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Email</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Link to clinic</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Actions</span>
              </DataTableRow>
            </DataTableHead>
            {rows.map((p) => {
              const cid = p.clinicId || p.clinic;
              const busy = practitionerBusyId === String(p._id);
              const linkTargets = (clinicsSummary || []).filter((c) => {
                if (!c?._id || String(c._id) === String(cid)) return false;
                const uid = p.user?._id || p.user;
                if (!uid) return false;
                const already = practitioners.some(
                  (o) =>
                    String(o.user?._id || o.user) === String(uid) &&
                    String(o.clinicId || o.clinic) === String(c._id)
                );
                return !already;
              });
              return (
                <DataTableItem key={p._id}>
                  <DataTableRow>{p.displayName || p.user?.email || p._id}</DataTableRow>
                  <DataTableRow>
                    <div style={{ minWidth: 220, maxWidth: 280 }}>
                      <ProfilePhotoBlock
                        clinicId={cid}
                        practitionerId={p._id}
                        photoUrl={p.photoUrl}
                        displayName={p.displayName || p.user?.email || ""}
                        disabled={busy}
                        onChanged={() => reloadAfterMutation()}
                      />
                    </div>
                  </DataTableRow>
                  <DataTableRow>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!p.teleconsultationAuthorized}
                      disabled={busy}
                      onChange={(e) => setPractitionerTcAuthorized(p, e.target.checked)}
                      aria-label="Teleconsultation authorized"
                    />
                  </DataTableRow>
                  <DataTableRow>
                    <div
                      className="d-flex flex-column"
                      style={{ minWidth: 140 }}
                      key={`langs-${p._id}-${(p.consultationLanguages || []).join(".")}-${p.primaryConsultationLanguage || ""}`}
                    >
                      <input
                        type="text"
                        className="form-control form-control-sm mb-1"
                        defaultValue={
                          Array.isArray(p.consultationLanguages) ? p.consultationLanguages.join(", ") : ""
                        }
                        placeholder="en, fr, it"
                        disabled={busy}
                        title="Spoken languages for teleconsultation (comma or space separated)"
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const prev = Array.isArray(p.consultationLanguages) ? p.consultationLanguages.join(", ") : "";
                          if (raw.trim() === prev.trim()) return;
                          const next = parseCommaSeparatedLangs(raw);
                          const same =
                            next.length === (p.consultationLanguages || []).length &&
                            next.every((c, i) => c === (p.consultationLanguages || [])[i]);
                          if (same) return;
                          updatePractitionerCompliance(p, { consultationLanguages: next });
                        }}
                        aria-label="Consultation languages"
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        defaultValue={p.primaryConsultationLanguage || ""}
                        placeholder="Primary"
                        maxLength={8}
                        disabled={busy}
                        title="Default language for consultation communications (e.g. en)"
                        onBlur={(e) => {
                          const v = e.target.value.trim().toLowerCase();
                          const prev = (p.primaryConsultationLanguage || "").trim().toLowerCase();
                          if (v === prev) return;
                          updatePractitionerCompliance(p, {
                            primaryConsultationLanguage: v ? v.slice(0, 8) : null,
                          });
                        }}
                        aria-label="Primary consultation language"
                      />
                    </div>
                  </DataTableRow>
                  <DataTableRow>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: 64 }}
                      defaultValue={p.practiceCountry || ""}
                      placeholder="FR"
                      maxLength={2}
                      disabled={busy}
                      onBlur={(e) => {
                        const v = e.target.value.trim().toUpperCase();
                        const prev = (p.practiceCountry || "").trim().toUpperCase();
                        if (v === prev) return;
                        if (v && !/^[A-Z]{2}$/.test(v)) {
                          swal("Validation", "Country must be a 2-letter ISO code (e.g. FR).", "warning");
                          e.target.value = p.practiceCountry || "";
                          return;
                        }
                        updatePractitionerCompliance(p, { practiceCountry: v || null });
                      }}
                      aria-label="Practice country ISO2"
                    />
                  </DataTableRow>
                  <DataTableRow>
                    <LicenseEditors
                      practiceCountry={p.practiceCountry}
                      licenses={p.licenses}
                      professionalId={p.professionalId}
                      medicalLicenceNumber={p.medicalLicenceNumber}
                      disabled={busy}
                      onSave={(licenses) => updatePractitionerCompliance(p, { licenses })}
                    />
                  </DataTableRow>
                  <DataTableRow>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ minWidth: 120 }}
                      key={`dip-list-${p._id}-${(p.diplomas || []).join("|")}`}
                      defaultValue={Array.isArray(p.diplomas) ? p.diplomas.join(", ") : ""}
                      placeholder="Comma / newline"
                      disabled={busy}
                      title="Structured diploma labels (comma or newline separated)"
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const prev = Array.isArray(p.diplomas) ? p.diplomas.join(", ") : "";
                        if (raw.trim() === prev.trim()) return;
                        const next = raw
                          .split(/[\n,]+/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const same =
                          next.length === (p.diplomas || []).length &&
                          next.every((c, i) => c === (p.diplomas || [])[i]);
                        if (same) return;
                        updatePractitionerCompliance(p, { diplomas: next });
                      }}
                      aria-label="Diploma list"
                    />
                  </DataTableRow>
                  <DataTableRow>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      style={{ minWidth: 140, resize: "vertical" }}
                      key={`cred-dip-${p._id}-${p.credentialDiplomas || ""}`}
                      defaultValue={p.credentialDiplomas || ""}
                      placeholder="Free-text diplomas / certifications"
                      disabled={busy}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const prev = (p.credentialDiplomas || "").trim();
                        if (v === prev) return;
                        updatePractitionerCompliance(p, { credentialDiplomas: v || null });
                      }}
                      aria-label="Credential diplomas notes"
                    />
                  </DataTableRow>
                  <DataTableRow>
                    <div className="d-flex flex-column align-items-start" style={{ minWidth: 112 }}>
                      {p.licenceDocumentUrl ? (
                        <>
                          <a
                            href={resolveBackendAssetUrl(p.licenceDocumentUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="small mb-1"
                          >
                            Open file
                          </a>
                          <Button
                            color="light"
                            size="sm"
                            className="py-0 px-2 small mb-1"
                            disabled={busy}
                            onClick={() => deleteLicenceDocument(p)}
                          >
                            Remove
                          </Button>
                        </>
                      ) : (
                        <span className="text-soft small mb-1">None</span>
                      )}
                      <label
                        className={`btn btn-light btn-sm py-0 px-2 mb-0 small ${busy ? "disabled" : ""}`}
                        style={{ cursor: busy ? "not-allowed" : "pointer" }}
                      >
                        <input
                          type="file"
                          className="d-none"
                          disabled={busy}
                          accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                          onChange={(ev) => {
                            const f = ev.target.files && ev.target.files[0];
                            ev.target.value = "";
                            if (f) uploadLicenceDocument(p, f);
                          }}
                        />
                        Upload
                      </label>
                    </div>
                  </DataTableRow>
                  <DataTableRow>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!p.credentialsVerified}
                      disabled={busy}
                      onChange={(e) => updatePractitionerCompliance(p, { credentialsVerified: e.target.checked })}
                      aria-label="I have verified this licence"
                      title="Check after confirming the registration number with the official registry"
                    />
                  </DataTableRow>
                  <DataTableRow>{p.role || "doctor"}</DataTableRow>
                  <DataTableRow>{p.user?.email || "—"}</DataTableRow>
                  <DataTableRow>
                    {linkTargets.length === 0 || !(p.user?._id || p.user) ? (
                      <span className="text-soft">—</span>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        defaultValue=""
                        disabled={busy}
                        onChange={(e) => {
                          const v = e.target.value;
                          e.target.value = "";
                          if (v) linkPractitionerToClinic(p, v);
                        }}
                        aria-label="Link practitioner to another clinic"
                      >
                        <option value="">Add to clinic…</option>
                        {linkTargets.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name || c._id}
                          </option>
                        ))}
                      </select>
                    )}
                  </DataTableRow>
                  <DataTableRow>
                    <Button
                      color="primary"
                      size="sm"
                      outline
                      className="mr-1"
                      disabled={busy}
                      onClick={() =>
                        history.push(getPublicUrl("teleconsultations/practitioner-approvals"))
                      }
                    >
                      Catalogue go-live
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      disabled={busy}
                      onClick={() => removePractitionerFromClinic(p)}
                    >
                      Remove
                    </Button>
                  </DataTableRow>
                </DataTableItem>
              );
            })}
          </DataTableBody>
        </DataTable>
      )}
    </>
  );

  if (loading) {
    return (
      <Content>
        <Head title="Clinic practitioners" />
        <Block>
          <p className="text-soft">Loading…</p>
        </Block>
      </Content>
    );
  }

  if (!clinicId || !clinic) {
    return (
      <Content>
        <Head title="Clinic practitioners" />
        <Block>
          <p>Clinic not found.</p>
          <Button color="light" onClick={navigateBackFromClinicContext}>
            {partnerId ? "Back to partner" : "Clinics list"}
          </Button>
        </Block>
      </Content>
    );
  }

  const doctors = practitioners.filter((p) => !isStaffRole(p.role));
  const staff = practitioners.filter((p) => isStaffRole(p.role));

  return (
    <Content>
      <Head title={`Practitioners — ${clinic.name || "Clinic"}`} />
      <Modal isOpen={addModalOpen} toggle={() => !addSubmitting && setAddModalOpen(false)} size="lg">
        <ModalHeader toggle={() => !addSubmitting && setAddModalOpen(false)}>Add practitioner to clinic</ModalHeader>
        <ModalBody>
          <p className="text-soft small mb-3">
            Link an existing practitioner account: search the partner roster (other clinics), verify an email against the
            database, or use the legacy id field. To onboard someone without an account, verify that the email is missing
            and use &quot;Send invitation&quot;.
          </p>

          <FormGroup>
            <Label className="form-label">From partner roster (other clinics)</Label>
            <Input
              type="text"
              className="mb-2"
              value={rosterSearch}
              onChange={(e) => setRosterSearch(e.target.value)}
              placeholder="Search by name or email…"
              disabled={addSubmitting}
            />
            <Input
              type="select"
              value={rosterPickUserId}
              onChange={(e) => {
                const v = e.target.value;
                setRosterPickUserId(v);
                setVerifiedUser(null);
              }}
              disabled={addSubmitting || rosterCandidates.length === 0}
            >
              <option value="">
                {rosterCandidates.length === 0 ? "No other linked practitioners on this partner" : "Select a practitioner…"}
              </option>
              {rosterCandidates.map((p) => {
                const uid = p.user?._id || p.user;
                const label = [p.displayName, p.user?.email].filter(Boolean).join(" — ") || String(uid);
                return (
                  <option key={`${p._id}-${uid}`} value={String(uid)}>
                    {label}
                  </option>
                );
              })}
            </Input>
          </FormGroup>

          <hr className="my-3" />

          <FormGroup>
            <Label className="form-label">Verify email or user id (database check)</Label>
            <div className="d-flex flex-wrap align-items-start">
              <Input
                type="text"
                className="flex-grow-1 mr-2 mb-2"
                style={{ minWidth: 200 }}
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
                placeholder="Email or 24-char user id"
                disabled={addSubmitting || verifyBusy}
              />
              <Button color="secondary" className="mb-2" type="button" disabled={addSubmitting || verifyBusy} onClick={verifyLookup}>
                {verifyBusy ? "Checking…" : "Verify"}
              </Button>
            </div>
            {verifiedUser ? (
              <div className="small mt-2 p-2 border rounded bg-light">
                <div>
                  <strong>{[verifiedUser.first_name, verifiedUser.last_name].filter(Boolean).join(" ") || "—"}</strong>
                </div>
                <div className="text-soft">{verifiedUser.email}</div>
                <div>
                  Role: <code>{verifiedUser.role}</code>
                  {verifiedUser.role === "practitioner" ? (
                    <span className="text-success ml-2">— OK to add</span>
                  ) : (
                    <span className="text-warning ml-2">— Not a practitioner account</span>
                  )}
                </div>
              </div>
            ) : null}
            {lookupQuery.trim().includes("@") ? (
              <div className="mt-2">
                <Button
                  color="light"
                  size="sm"
                  type="button"
                  disabled={addSubmitting || inviteBusy}
                  onClick={sendInvitationToEmail}
                >
                  {inviteBusy ? "Sending…" : "Send invitation to this email"}
                </Button>
                <span className="text-soft small ml-2">Use when the account does not exist yet (after Verify returns not found).</span>
              </div>
            ) : null}
          </FormGroup>

          <hr className="my-3" />

          <FormGroup>
            <Label className="form-label">Legacy: user id or email (optional)</Label>
            <Input
              type="text"
              value={addForm.userIdOrEmail}
              onChange={(e) => setAddForm((f) => ({ ...f, userIdOrEmail: e.target.value }))}
              placeholder="24-char Mongo id or email (resolved if roster / verify not used)"
            />
          </FormGroup>
          <FormGroup>
            <Label className="form-label">Role</Label>
            <Input
              type="select"
              value={addForm.role}
              onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
            >
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="assistant">Assistant</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label className="form-label">Override display name (optional)</Label>
            <Input
              type="text"
              value={addForm.displayName}
              onChange={(e) => setAddForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="Optional"
            />
          </FormGroup>
          <FormGroup>
            <Label className="form-label">Diplomas (one per line or comma-separated)</Label>
            <Input
              type="textarea"
              rows={3}
              value={addForm.diplomas}
              onChange={(e) => setAddForm((f) => ({ ...f, diplomas: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label className="form-label">Teleconsultation languages (optional)</Label>
            <Input
              type="text"
              value={addForm.consultationLanguages}
              onChange={(e) => setAddForm((f) => ({ ...f, consultationLanguages: e.target.value }))}
              placeholder="e.g. en, fr, it"
            />
            <span className="text-soft small">Comma or space separated (ISO-style codes, max 8 chars each).</span>
          </FormGroup>
          <FormGroup>
            <Label className="form-label">Primary consultation language (optional)</Label>
            <Input
              type="text"
              value={addForm.primaryConsultationLanguage}
              onChange={(e) => setAddForm((f) => ({ ...f, primaryConsultationLanguage: e.target.value }))}
              placeholder="e.g. en"
              maxLength={8}
            />
          </FormGroup>
          <div className="d-flex justify-content-end flex-wrap">
            <Button color="light" className="mr-2 mb-2" type="button" disabled={addSubmitting} onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="button" disabled={addSubmitting} className="mb-2" onClick={submitAddPractitioner}>
              {addSubmitting ? "Saving…" : "Join this clinic"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <BlockHead size="sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h3">Practitioners (admin)</BlockTitle>
            <BlockDes className="text-soft">
              Clinic: <strong>{clinic.name || "—"}</strong>. Profile photo, teleconsultation toggle, practice country, licence identifiers,
              diploma list and notes, licence document upload / link, credentials verified flag, languages, link to another clinic,
              or remove from this clinic.
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent className="mb-xs-3 d-flex flex-wrap align-items-center">
            <Button color="light" size="sm" className="mr-2 mb-2" onClick={navigateBackFromClinicContext}>
              {partnerId ? "Back to partner" : "Clinics list"}
            </Button>
            <Button color="light" outline size="sm" className="mr-2 mb-2" onClick={() => history.push(getPublicUrl("clinics"))}>
              All clinics
            </Button>
            <Button color="primary" size="sm" className="mr-2 mb-2" onClick={openAddModal}>
              Add practitioner
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      {legacyWithoutUserCount > 0 ? (
        <Alert color="warning" className="mb-3" role="status">
          <strong>Action required:</strong> {legacyWithoutUserCount} practitioner record(s) on this clinic have no linked user
          account (legacy rows). New practitioners must have an account; link one from the partner team flow or support. Rows are
          not auto-deleted.
        </Alert>
      ) : null}

      <Block>
        {renderTable(doctors, "Practitioners")}
        {renderTable(staff, "Staff")}
      </Block>
    </Content>
  );
};

export default ClinicPractitionersAdmin;
